import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { AuthService } from '../service/auth/auth.service';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { isCapacitorNative } from '../service/platform/capacitor-platform';
import { PlatformService } from '../service/platform/platform.service';
import { SidebarService } from '../service/sidebar/sidebar.service';
import { DashboardMilestone } from '../service/rest/rest.service';
import { UserMilestoneBadgesService } from '../service/user-milestone-badges.service';
import { BrandService } from '../service/brand/brand.service';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.css'
})
export class NavbarComponent implements OnInit, OnDestroy {
    protected readonly location = location;
    userName:string|undefined;
    userLogined: boolean = false;
    isAdmin: boolean = false;
    recentMilestones: DashboardMilestone[] = [];
    readonly isNative = isCapacitorNative();
    private readonly platform = inject(PlatformService);
    /** Burger taps from any tab page open the same drawer the dashboard uses. */
    protected readonly sidebar = inject(SidebarService);
    protected readonly brand = inject(BrandService);
    currentUrl: string;
    private routerSub: Subscription | null = null;
    private badgesSub: Subscription | null = null;

    constructor(
      private authService: AuthService,
      private router: Router,
      private ngLocation: Location,
      private milestoneBadgesService: UserMilestoneBadgesService) {
      if(authService.isAuthenticated()){
        this.userLogined = true;
        const user = authService.getUser();
        this.userName = user?.name;
        this.isAdmin = !!user?.admin;
      }
      this.currentUrl = router.url;
    }

    ngOnInit(): void {
      this.routerSub = this.router.events
        .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
        .subscribe((e) => (this.currentUrl = e.urlAfterRedirects));
      this.badgesSub = this.milestoneBadgesService.recentMilestones$.subscribe((milestones) => {
        this.recentMilestones = milestones.slice(0, 4);
      });
    }

    ngOnDestroy(): void {
      this.routerSub?.unsubscribe();
      this.badgesSub?.unsubscribe();
    }

    /**
     * True only on iOS. Android has a guaranteed system-back gesture that
     * Capacitor routes to the same handler, so an extra header arrow there
     * is duplicate chrome (Material 3 convention). The test runner is also
     * excluded — tests own a cancel-with-confirm flow, and a free back
     * arrow would lose progress silently.
     */
    canGoBack(): boolean {
      if (!this.platform.isIos()) return false;
      const path = (this.currentUrl || '/').split('?')[0].split('#')[0];
      if (path === '/' || path === '/dashboard' || path === '/admin') return false;
      if (path.startsWith('/question-pool-study') || path.startsWith('/question-pool-exam')) return false;
      return true;
    }

    /**
     * Walk one step up the navigation stack. Fall back to /dashboard when there is
     * no history (deep-link cold start), otherwise Location.back() would exit the app.
     */
    goBack(): void {
      if (typeof window !== 'undefined' && window.history.length > 1) {
        this.ngLocation.back();
        return;
      }
      void this.router.navigate(['/dashboard']);
    }

    toggleSidebar(): void {
      this.sidebar.toggle();
    }

    navigateToTrainer(event: Event) {
      event.preventDefault(); // Prevent the default anchor click action
      this.router.navigate(['question-pool'], { skipLocationChange: true });
    }
}
